/**
 * Unified Model Client
 * Supports both Ollama API and Cloudflare Modelfire
 */

import { CLOUDFLARE_MODELS, DEFAULT_MODELS, getAllTextModels } from '../config/cloudflare-models.js';
import { getAllModelsFlat } from '../config/auto-generate-models.js';

export const MODEL_PROVIDERS = {
  OLLAMA: 'ollama',
  CLOUDFLARE: 'cloudflare'
};

export class UnifiedModelClient {
  constructor(config) {
    this.ollamaBaseUrl = config.ollamaBaseUrl || 'https://ollama-api.damineweb.work';
    this.cloudflareWorkerUrl = config.cloudflareWorkerUrl;
    this.cloudflareAccountId = config.cloudflareAccountId;
    this.cloudflareApiToken = config.cloudflareApiToken;

    // Use worker as proxy for Ollama to avoid CORS
    this.ollamaProxyUrl = config.cloudflareWorkerUrl ?
      `${config.cloudflareWorkerUrl}/ollama` :
      this.ollamaBaseUrl;

    this.availableModels = {
      ollama: [],
      cloudflare: []
    };
  }

  /**
   * Fetch available models from both providers
   */
  async fetchAvailableModels() {
    try {
      // Fetch Ollama models (via worker proxy to avoid CORS)
      const ollamaResponse = await fetch(`${this.ollamaProxyUrl}/api/tags`);

      if (!ollamaResponse.ok) {
        console.warn('Ollama API not accessible:', ollamaResponse.status);
        // Return ALL Cloudflare models if Ollama fails
        this.availableModels.ollama = [];
        this.availableModels.cloudflare = getAllModelsFlat()
          .map(model => ({
            ...model,
            provider: MODEL_PROVIDERS.CLOUDFLARE
          }));

        console.log(`[UnifiedClient] ✅ Loaded ${this.availableModels.cloudflare.length} Cloudflare models`);

        return {
          ollama: [],
          cloudflare: this.availableModels.cloudflare,
          total: this.availableModels.cloudflare.length
        };
      }

      const ollamaData = await ollamaResponse.json();

      this.availableModels.ollama = ollamaData.models.map(model => ({
        id: model.name,
        name: model.name,
        provider: MODEL_PROVIDERS.OLLAMA,
        size: model.size,
        modified: model.modified_at,
        capabilities: this.detectCapabilities(model.name)
      }));

      // Cloudflare models (ALL 134+ models!)
      this.availableModels.cloudflare = getAllModelsFlat()
        .map(model => ({
          ...model,
          provider: MODEL_PROVIDERS.CLOUDFLARE
        }));

      console.log(`[UnifiedClient] ✅ Loaded ${this.availableModels.ollama.length} Ollama + ${this.availableModels.cloudflare.length} Cloudflare models`);

      return {
        ollama: this.availableModels.ollama,
        cloudflare: this.availableModels.cloudflare,
        total: this.availableModels.ollama.length + this.availableModels.cloudflare.length
      };
    } catch (error) {
      console.error('Failed to fetch models:', error);
      return { ollama: [], cloudflare: [], total: 0 };
    }
  }

  /**
   * Detect model capabilities from name
   */
  detectCapabilities(modelName) {
    const name = modelName.toLowerCase();
    const capabilities = [];

    if (name.includes('vision') || name.includes('vl') || name.includes('image')) {
      capabilities.push('vision');
    }
    if (name.includes('code') || name.includes('coder')) {
      capabilities.push('coding');
    }
    if (name.includes('thinking') || name.includes('reasoning')) {
      capabilities.push('reasoning');
    }
    if (name.includes('deepseek') || name.includes('qwen') || name.includes('nemotron')) {
      capabilities.push('advanced');
    }

    return capabilities;
  }

  /**
   * Run inference with automatic provider selection
   */
  async runInference(modelId, messages, options = {}) {
    const provider = this.detectProvider(modelId);

    if (provider === MODEL_PROVIDERS.OLLAMA) {
      return await this.runOllamaInference(modelId, messages, options);
    } else {
      return await this.runCloudflareInference(modelId, messages, options);
    }
  }

  /**
   * Detect which provider a model belongs to
   */
  detectProvider(modelId) {
    // Check if it's a Cloudflare model (starts with @)
    if (modelId.startsWith('@cf/') || modelId.startsWith('@hf/')) {
      return MODEL_PROVIDERS.CLOUDFLARE;
    }
    // Otherwise assume Ollama
    return MODEL_PROVIDERS.OLLAMA;
  }

  /**
   * Run Ollama inference
   */
  async runOllamaInference(modelId, messages, options = {}) {
    try {
      // Use worker proxy to avoid CORS issues
      const response = await fetch(`${this.ollamaProxyUrl}/api/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          model: modelId,
          messages: messages.map(msg => ({
            role: msg.role,
            content: msg.content
          })),
          stream: options.stream || false,
          options: {
            temperature: options.temperature || 0.7,
            num_predict: options.maxTokens || 4000,
            top_p: options.topP || 0.9
          }
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(`Ollama API Error: ${error.error || response.statusText}`);
      }

      if (options.stream) {
        return this.handleOllamaStream(response);
      }

      const data = await response.json();

      return {
        provider: MODEL_PROVIDERS.OLLAMA,
        model: modelId,
        content: data.message?.content || '',
        done: data.done,
        usage: {
          prompt_tokens: data.prompt_eval_count || 0,
          completion_tokens: data.eval_count || 0,
          total_tokens: (data.prompt_eval_count || 0) + (data.eval_count || 0)
        }
      };
    } catch (error) {
      console.error('Ollama inference error:', error);
      throw error;
    }
  }

  /**
   * Handle Ollama streaming response
   */
  async *handleOllamaStream(response) {
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (!line.trim()) continue;

          try {
            const data = JSON.parse(line);
            if (data.message?.content) {
              yield data.message.content;
            }
          } catch (e) {
            console.warn('Failed to parse stream chunk:', e);
          }
        }
      }
    } finally {
      reader.releaseLock();
    }
  }

  /**
   * Run Cloudflare inference
   */
  async runCloudflareInference(modelId, messages, options = {}) {
    try {
      const url = `${this.cloudflareWorkerUrl}/ai/run/${modelId}`;

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          messages: messages.map(m => ({ role: m.role, content: m.content })),
          stream: options.stream || false,
          max_tokens: options.maxTokens || 4000,
          temperature: options.temperature || 0.7,
          top_p: options.topP || 0.9
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(`Cloudflare API Error: ${error.error || response.statusText}`);
      }

      if (options.stream) {
        return this.handleCloudflareStream(response);
      }

      const data = await response.json();

      // Extract response
      let content = '';
      if (data.result?.response) {
        content = data.result.response;
      } else if (data.result?.choices?.[0]?.message?.content) {
        content = data.result.choices[0].message.content;
      }

      return {
        provider: MODEL_PROVIDERS.CLOUDFLARE,
        model: modelId,
        content,
        usage: data.result?.usage || {}
      };
    } catch (error) {
      console.error('Cloudflare inference error:', error);
      throw error;
    }
  }

  /**
   * Handle Cloudflare streaming
   */
  async *handleCloudflareStream(response) {
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (!line.trim() || !line.startsWith('data: ')) continue;

          const data = line.slice(6);
          if (data === '[DONE]') continue;

          try {
            const parsed = JSON.parse(data);
            if (parsed.response) {
              yield parsed.response;
            }
          } catch (e) {
            console.warn('Failed to parse stream chunk:', e);
          }
        }
      }
    } finally {
      reader.releaseLock();
    }
  }

  /**
   * Get recommended models by task
   */
  getRecommendedModels(task) {
    const recommendations = {
      coding: [
        'deepseek-v4-pro:cloud',
        'qwen3-coder:480b-cloud',
        'kimi-k2.7-code:cloud',
        '@cf/deepseek-ai/deepseek-coder-6.7b-instruct-awq'
      ],
      vision: [
        'qwen3-vl:235b-cloud',
        'x/z-image-turbo:latest',
        '@cf/meta/llama-3.2-11b-vision-instruct'
      ],
      reasoning: [
        'deepseek-v4-flash:cloud',
        'kimi-k2-thinking:cloud',
        'qwen3.5:397b-cloud'
      ],
      general: [
        'qwen3.5:cloud',
        'mistral-large-3:675b-cloud',
        '@cf/meta/llama-3.3-70b-instruct-fp8-fast'
      ],
      fast: [
        'ministral-3:8b-cloud',
        'llama3.2:3b',
        '@cf/meta/llama-3.2-3b-instruct'
      ]
    };

    return recommendations[task] || recommendations.general;
  }
}

/**
 * Singleton instance
 */
let clientInstance = null;

export function initializeUnifiedClient(config) {
  clientInstance = new UnifiedModelClient(config);
  return clientInstance;
}

export function getUnifiedClient() {
  if (!clientInstance) {
    throw new Error('Unified client not initialized. Call initializeUnifiedClient first.');
  }
  return clientInstance;
}
